import type {FieldPolicy} from "@apollo/client"
import {match, P} from "ts-pattern"

type Keys = Array<string | Keys>

/**
 * Options for controlling pagination cache keys
 *
 * @see https://www.apollographql.com/docs/react/pagination/core-api
 */
interface PaginateOffsetArgs {
  /**
   * List of keys to omit from the cacheKey, defaults to `['offset']` and this is likely all you need.
   * @see https://www.apollographql.com/docs/react/caching/cache-field-behavior
   */
  keyArgsOmit?: string[]
  /**
   * List of keys to include in the cacheKey, you likely want `keyArgsOmit` otherwise new keys added to querys may unintentionally create separate cache objects
   * @see https://www.apollographql.com/docs/react/caching/cache-field-behavior
   */
  keysArgs?: Keys
}

const defaultKeysArgs: Keys = ["offset"]

/**
 * Paginate Offset for use with apollo client `typePolicies`
 *
 * This provides a wrapper around `keyArgs` and `merge` in a style which aligns with our offset pagination approach (i.e schema using `list.schema('Entity', EntityList'))`
 *
 * @see https://www.apollographql.com/docs/react/pagination/core-api/#offset-based-pagination
 * @throws when schema for pagination is used incorrectly, this is intended to highlight bad configuration sooner than silently broken pagination
 */
export function paginateOffset(paginateOptions: PaginateOffsetArgs = {}): FieldPolicy<unknown> {
  return {
    keyArgs: (args, context) => {
      if (!args) {
        console.error('error in paginateOffset for"', context)
        throw new Error("expected paginateOffset to be used with arguments")
      }
      const {keyArgsOmit = defaultKeysArgs, keysArgs} = paginateOptions

      return match({keyArgsOmit, keysArgs})
        .with({keysArgs: P.select(P.nonNullable)}, (keep) => keep)
        .with({keyArgsOmit: P.select(P.nonNullable)}, (omit) => Object.keys(args).filter((key) => !omit?.includes(key)))
        .exhaustive()
    },

    merge(existing, incoming, options) {
      const {args} = options
      if (!args) {
        console.error("error in paginateOffset for", options)
        throw new Error("expected paginateOffset to be used with arguments")
      }

      return match([existing, incoming])
        .with([undefined, {results: P.array(), offset: P.number, hasMore: P.boolean}], ([_, data]) => data)
        .with(
          [{results: P.array()}, {results: P.array(), offset: P.number, hasMore: P.boolean}],
          ([{results: existingResults}, latest]) => {
            const offset = args.offset ?? 0
            const merged = existingResults.slice(0)

            for (let i = 0; i < latest.results.length; ++i) {
              merged[offset + i] = latest.results[i]
            }

            return {
              ...(existing || {}),
              ...latest,
              results: merged
            }
          }
        )
        .otherwise((data) => {
          console.error(
            "error in paginateOffset. Pagination requests must be of the form {results: [], offset: number, hasMore: boolean}",
            ...data
          )
          throw new Error("Pagination Developer Error")
        })
    }
  }
}
